/**
 * Pending-invitation hooks (PR-E).
 *
 * Reads the current user's pending CustomerOrg memberships
 * (``GET /api/v1/invitations/pending``) and exposes accept / decline
 * mutations for the unified ``/invitations/accept`` page.
 *
 * On accept we invalidate ``/auth/me`` so the new B2B membership
 * shows up in the AccountMenu without a hard refresh; on decline we
 * just refetch the pending list.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import type {
  InvitationAcceptResponse,
  InvitationDeclineResponse,
  OrgMemberAcceptResponse,
  PendingInvitation,
} from "@/api/types";
import { apiFetch } from "@/lib/api";

import { adminKeys } from "./keys";

const API = "/api/v1";

const PENDING_KEY = ["invitations", "pending"] as const;

export function usePendingInvitations(
  options?: Omit<UseQueryOptions<PendingInvitation[]>, "queryKey" | "queryFn">,
) {
  return useQuery<PendingInvitation[]>({
    queryKey: PENDING_KEY,
    queryFn: () =>
      apiFetch<PendingInvitation[]>(`${API}/invitations/pending`),
    // Pending invites are typically read on /account / AccountMenu
    // mount; 60s is fresh enough that the user sees a freshly-issued
    // invite within a single browser session without aggressive
    // polling.
    staleTime: 60 * 1000,
    retry: false,
    ...options,
  });
}


export function useAcceptInvitation(
  options?: Omit<
    UseMutationOptions<InvitationAcceptResponse, Error, number>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<InvitationAcceptResponse, Error, number>({
    mutationFn: (memberId) =>
      apiFetch<InvitationAcceptResponse>(
        `${API}/invitations/${memberId}/accept`,
        { method: "POST" },
      ),
    ...options,
    onSuccess: async (data, vars, onMutateRes, ctx) => {
      qc.invalidateQueries({ queryKey: PENDING_KEY });
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, vars, onMutateRes, ctx);
    },
  });
}


export function useDeclineInvitation(
  options?: Omit<
    UseMutationOptions<InvitationDeclineResponse, Error, number>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<InvitationDeclineResponse, Error, number>({
    mutationFn: (memberId) =>
      apiFetch<InvitationDeclineResponse>(
        `${API}/invitations/${memberId}/decline`,
        { method: "POST" },
      ),
    ...options,
    onSuccess: async (data, vars, onMutateRes, ctx) => {
      qc.invalidateQueries({ queryKey: PENDING_KEY });
      await options?.onSuccess?.(data, vars, onMutateRes, ctx);
    },
  });
}


// PR-E2: tenant-team invite acceptance / decline. Distinct paths so the
// BE can return the right response shape (org_id vs customer_org_id)
// and so the audit log captures the right action verb.

export function useAcceptOrgMemberInvitation(
  options?: Omit<
    UseMutationOptions<
      OrgMemberAcceptResponse,
      Error,
      { memberId: number; inviteToken: string }
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    OrgMemberAcceptResponse,
    Error,
    { memberId: number; inviteToken: string }
  >({
    mutationFn: ({ memberId, inviteToken }) =>
      apiFetch<OrgMemberAcceptResponse>(
        `${API}/invitations/org-member/${memberId}/accept`,
        { method: "POST", body: { invite_token: inviteToken } },
      ),
    ...options,
    onSuccess: async (data, vars, onMutateRes, ctx) => {
      qc.invalidateQueries({ queryKey: PENDING_KEY });
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, vars, onMutateRes, ctx);
    },
  });
}


export function useDeclineOrgMemberInvitation(
  options?: Omit<
    UseMutationOptions<InvitationDeclineResponse, Error, number>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<InvitationDeclineResponse, Error, number>({
    mutationFn: (memberId) =>
      apiFetch<InvitationDeclineResponse>(
        `${API}/invitations/org-member/${memberId}/decline`,
        { method: "POST" },
      ),
    ...options,
    onSuccess: async (data, vars, onMutateRes, ctx) => {
      qc.invalidateQueries({ queryKey: PENDING_KEY });
      await options?.onSuccess?.(data, vars, onMutateRes, ctx);
    },
  });
}
